import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import SVGIcon from 'reusableComponents/SVGIcon'
import { selectIsAuthenticated } from 'store/shipping/shippingSelectors'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import { verifyRecipientSession } from 'api/shipping'
import { setCookieToLocalStorage } from 'utils/helperFunctions'
import PackageDeliveryPreferences from 'components/PackageDeliveryPreferences'
import {
  setIsGuestMode,
  setIsVerifiedUser,
  setAllowRenewSession,
} from 'store/shipping/shippingSlice'
import { ReactComponent as CalendarDaysIcon } from 'assets/svg/calendar-days.svg'
import { ReactComponent as ChevronDownIcon } from 'assets/svg/chevron-down.svg'
import { colors } from 'globalStyles'
import { isValid, parseISO, startOfDay } from 'date-fns'
import Tooltip from 'reusableComponents/ToolTip'
import constants from 'constants.json'
import {
  Card,
  Header,
  HeaderWrapper,
  PackageInfo,
  TrackingNumber,
  VendorLogo,
  DeliveryInfo,
  EstimatedDelivery,
  Status,
  DeliveryDate,
  IconWrapper,
  DateText,
  Address,
  PreferencesSection,
  PreferencesButton,
  ChevronIcon,
} from './ShipmentCard.style'

const truncateVendorName = (name) => {
  if (name && name.length > 24) {
    return `${name.substring(0, 24)}...`
  }
  return name
}

const getShipmentStatus = (shipment) => {
  switch (shipment.status) {
    case 'POP':
    case 'IND':
    case 'SDR':
      return 'Picked Up'
    case 'OFD':
      return 'Out For Delivery'
    case 'DEL':
      return 'Delivered'
    case 'ATT':
      return 'Delayed'
    default:
      return ''
  }
}

const getVendorLogo = (vendorName) => {
  if (!vendorName) return null
  // This is a TEMP solution because we cannot support loading much more logos in our assets folder
  // We will need to load the logos from the backend in the future
  const imageName = vendorName.replaceAll(' ', '-')
  const imagePath = `${process.env.PUBLIC_URL}/assets/logo/${imageName}.svg`

  // Create a hidden image to test if the logo exists
  const img = new Image()
  img.src = imagePath

  // Only return the img element if the logo loads successfully
  return new Promise((resolve) => {
    img.onload = () => resolve(<img alt={vendorName} src={imagePath} />)
    img.onerror = () => resolve(null)
  })
}

// Add these helper functions (copied from DeliveryDate.jsx)
const formatDateTime = (dateTime, t) => {
  if (!dateTime) return ''
  const splitDateTime = dateTime.split(' ')
  if (splitDateTime.length === 2 && splitDateTime[1]) {
    return t('delivery_date_time', { date: dateTime })
  }
  if (splitDateTime.length === 3) {
    return dateTime
  }
  if (splitDateTime.length === 4) {
    return t('tracking.estimated_delivery_evening', { date: dateTime })
  }
  // Temporary fix for the issue of the package details having wrong date
  // TODO: Change the date time based on destination time zone
  const originDate = parseISO(dateTime)
  return t('date_without_ordinal', { date: originDate })
}

const isPastEstimatedDeliveryDate = (deliveryDate) => {
  if (!deliveryDate) return false

  const parsedDate = parseISO(deliveryDate.split(/[T ]/)[0])
  if (!isValid(parsedDate)) return false

  return startOfDay(parsedDate) < startOfDay(new Date())
}

const shouldShowPendingEstimatedDelivery = (shipment) =>
  shipment.status?.toUpperCase() !== 'DEL' &&
  isPastEstimatedDeliveryDate(shipment.estimateddeliverydate)

const formatEstimatedDeliveryDate = (deliveryDate, t, showPendingDeliveryDate = false) => {
  const { DELIVERY_DATE_RECALCULATING } = constants
  const { BUSINESS_DELIVERY_DATE } = constants
  if (!deliveryDate || deliveryDate === DELIVERY_DATE_RECALCULATING || showPendingDeliveryDate) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <span>{t('tracking.not_available_pending')}</span>
        <Tooltip id="tooltip-edd-not-available" placement="bottom">
          {t('tracking.not_available_pending_tooltip')}
        </Tooltip>
      </div>
    )
  }
  if (deliveryDate.includes(BUSINESS_DELIVERY_DATE)) {
    return deliveryDate.replace(BUSINESS_DELIVERY_DATE, t('tracking.business_days'))
  }
  return formatDateTime(deliveryDate, t)
}

const ShipmentCard = ({ shipment, packageNumber, totalPackages }) => {
  const { t } = useTranslation()
  const { lang } = useParams()
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false)
  const [vendorLogo, setVendorLogo] = useState(null)
  const status = getShipmentStatus(shipment)
  const currentPackage = packageNumber || 1
  const packageCount = totalPackages || 1
  const showPendingDeliveryDate = shouldShowPendingEstimatedDelivery(shipment)

  // Use the new formatting logic:
  const displayEstimateDeliveryDate = formatEstimatedDeliveryDate(
    shipment.estimateddeliverydate,
    t,
    showPendingDeliveryDate
  )

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const onClickPackageCount = async () => {
    if (isAuthenticated) {
      try {
        const dataToByPassOtpVerification = await verifyRecipientSession(shipment.shipmentid, lang)
        const verifyData = dataToByPassOtpVerification.data.sessionData
        if (verifyData.response === 'SESSION_CREATED') {
          const { sessionUUID, expiryTime, updateExpTime } = verifyData
          setCookieToLocalStorage(shipment.pinhash, sessionUUID, expiryTime)
          dispatch(setIsVerifiedUser(true))
          dispatch(setAllowRenewSession(updateExpTime))
          dispatch(setIsGuestMode(true))
          navigate(`/rs/tr/${lang}/${shipment.pinhash}`)
        } else {
          dispatch(setIsVerifiedUser(false))
          dispatch(setIsGuestMode(false))
        }
      } catch (error) {
        // Removed console.error
      }
    }
  }

  const packageDeliveryData = {
    signaturePreference: shipment.remote_signature_preference,
    adultSignatureRequired: shipment.adultsignaturerequired,
    signatureRequired: shipment.signaturerequired,
    chainOfSignature: shipment.chainofsignature,
    hasSignatureException: shipment.hassignatureexception,
    expressChequeFlag: shipment.expresschequeflag,
    dangerousGoods: shipment.dangerousgoods,
    dgType: shipment.dgtype,
    locationPreference: shipment.delivery_preference,
    shipmentid: shipment.shipmentid,
    pinhash: shipment.pinhash,
  }

  useEffect(() => {
    if (shipment.vendorname) {
      getVendorLogo(shipment.vendorname).then(setVendorLogo)
    }
  }, [shipment.vendorname])

  return (
    <Card>
      <HeaderWrapper>
        <Header>
          <PackageInfo>
            <Button variant="link" onClick={onClickPackageCount}>
              {t('shipment_card.package_count', { current: currentPackage, total: packageCount })}
            </Button>
            <TrackingNumber>
              {t('shipment_card.tracking_pin', { pin: shipment.packagepin })}
            </TrackingNumber>
          </PackageInfo>
          {shipment.vendorname && (
            <VendorLogo>{vendorLogo || truncateVendorName(shipment.vendorname)}</VendorLogo>
          )}
        </Header>
      </HeaderWrapper>
      <DeliveryInfo>
        <EstimatedDelivery>
          {t('shipment_card.estimated_delivery')}
          {status && (
            <Status status={status}>
              {t(`status_progress_bar.${status.toLowerCase().replaceAll(' ', '_')}`)}
            </Status>
          )}
        </EstimatedDelivery>
        <DeliveryDate>
          <IconWrapper>
            <SVGIcon Icon={CalendarDaysIcon} color={colors.secondary_light_blue} />
          </IconWrapper>
          {/* Use the new display logic here */}
          <DateText>{displayEstimateDeliveryDate}</DateText>
        </DeliveryDate>
        <Address>
          {t('shipment_card.deliver_to', {
            city: shipment.city,
            province: shipment.province,
            interpolation: { escapeValue: false },
          })}
        </Address>
      </DeliveryInfo>

      <PreferencesSection>
        <PreferencesButton onClick={() => setIsPreferencesOpen(!isPreferencesOpen)}>
          {t('myPreferences.my_delivery_pref')}
          <ChevronIcon isOpen={isPreferencesOpen}>
            <SVGIcon Icon={ChevronDownIcon} />
          </ChevronIcon>
        </PreferencesButton>
        {isPreferencesOpen && (
          <Header>
            <PackageDeliveryPreferences packageDeliveryDetails={packageDeliveryData} />
          </Header>
        )}
      </PreferencesSection>
    </Card>
  )
}

ShipmentCard.propTypes = {
  shipment: PropTypes.shape({
    shipmentid: PropTypes.string.isRequired,
    shipmentpin: PropTypes.string.isRequired,
    packagepin: PropTypes.string.isRequired,
    deliverydate: PropTypes.string.isRequired,
    estimateddeliverydate: PropTypes.string,
    status: PropTypes.string,
    city: PropTypes.string.isRequired,
    province: PropTypes.string.isRequired,
    vendorname: PropTypes.string,
    shipmentpiecetotalcount: PropTypes.number.isRequired,
    haspickedup: PropTypes.bool.isRequired,
    delivery_preference: PropTypes.string,
    additional_delivery_instructions: PropTypes.string,
    buzzer_code: PropTypes.string,
    returnedtosender: PropTypes.bool.isRequired,
    pinhash: PropTypes.string.isRequired,
    remote_signature_preference: PropTypes.string,
    adultsignaturerequired: PropTypes.bool.isRequired,
    signaturerequired: PropTypes.bool.isRequired,
    chainofsignature: PropTypes.bool.isRequired,
    hassignatureexception: PropTypes.bool.isRequired,
    expresschequeflag: PropTypes.bool.isRequired,
    dangerousgoods: PropTypes.bool.isRequired,
    dgtype: PropTypes.string.isRequired,
  }).isRequired,
  packageNumber: PropTypes.number,
  totalPackages: PropTypes.number,
}

ShipmentCard.defaultProps = {
  packageNumber: 1,
  totalPackages: 1,
}

export default ShipmentCard
